import { Form as FormikForm, Formik, FormikErrors, FormikProps, FormikTouched } from 'formik';
import React, { useEffect, useState } from 'react';
import { Col, Row, Stack } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import * as Yup from 'yup';
import { BackButton } from '../../../../components/buttons/BackButton';
import { SimpleButton } from '../../../../components/buttons/SimpleButton';
import Stepper from '../../../../components/stepper/Stepper';
import { useDataContext } from '../../../../context';
import { useToast } from '../../../../context/ToastContext';
import {
  useGetParentQuery,
  useUpdateParentMutation
} from '../../../../services/endpoints/people/parent';
import {
  useCreateUserMutation,
  useGetAdminQuery,
  useGetInstructorQuery,
  UserType,
  useUpdateSFCMutation
} from '../../../../services/endpoints/people/user';
import { getCurrentUser } from '../../../../services/helper';
import { phoneRegExp, userTypes, usTimezones } from '../../../../utils/consts';
import { userTypeNames } from '../../../../utils/consts/index';
import { userTypeToAdminLevel } from '../../../../utils/privilegeEscalation';
import { getErrorMessage, getKeyByValue } from '../../../../utils/utils';
import { FadeIn } from '../../../animations/FadeIn';
import { UserForm } from './UserForm';

const steps = [{ name: 'Details', form: UserForm }];

export interface FormValues {
  id?: number;
  full_name?: string;
  first_name: string;
  last_name: string;
  email: string;
  password: string;
  confirm_password?: string;
  phone: string;
  address: string;
  address2?: string;
  city: string;
  state: string;
  zip: string;
  timezone: string;
  location_id: any; //{value: number, label: string}
  user_type: string;
  opts_out_of_texts?: boolean;
  instructor_session_rate?: number;
  instructor_session_reimbursement?: number;
  instructor_online_rate?: number;
  instructor_online_reimbursement?: number;
  trainee_rate?: number;
  observer_rate?: number;
  admin_level?: string;
  active: boolean;
  uses_advanced_scheduling?: boolean;
  user_type_id?: number;
  test_email_recipient?: boolean;
}

export interface UpdateFormUserSFC {
  id?: number;
  full_name?: string;
  first_name: string;
  last_name: string;
  email: string;
  password: string;
  confirm_password?: string;
  phone: string;
  address: string;
  address2?: string;
  city: string;
  state: string;
  zip: string;
  timezone: string;
  location_id: any; //{value: number, label: string}
  user_type: string;
  opts_out_of_texts?: boolean;
  instructor_session_rate?: number;
  instructor_session_reimbursement?: number;
  instructor_online_rate?: number;
  instructor_online_reimbursement?: number;
  test_email_recipient?: boolean;
  trainee_rate?: number;
  observer_rate?: number;
  admin_level?: string;
  active: boolean;
  uses_advanced_scheduling?: boolean;
  user_type_id?: number;
}

export interface StepProps {
  errors: Partial<FormikErrors<FormValues>>;
  touched: Partial<FormikTouched<FormValues>>;
  values?: FormValues;
  setFieldValue?: (field: string, value: any, shouldValidate?: boolean) => void;
  readOnly?: boolean;
  userType?: UserType;
  editing?: boolean;
}

const userLinksMap = (user: UserType, id?: number | string) => {
  if (user === 'customer') {
    // TODO: add customer values

    return {
      edit: `/admin/sfc-users/parents/${id}/edit`,
      detail: `/admin/sfc-users/parents/${id}`
    };
  } else if (user === 'instructor') {
    return {
      edit: `/admin/dash-users/instructors/${id}/edit`,
      detail: `/admin/dash-users/instructors/${id}`
    };
  } else {
    return {
      edit: `/admin/dash-users/admins/${id}/edit`,
      detail: `/admin/dash-users/admins/${id}`
    };
  }
};

interface AddEditUserProps {
  editing?: boolean;
  userType: UserType;
}

const getDefaultLocationId = (
  editing: boolean | undefined,
  userType: UserType,
  getLocationTitle: (locationId: number | string) => string
): FormValues['location_id'] => {
  if (editing || userType === 'customer') {
    return null;
  }

  const locationId = getCurrentUser()?.location_id;
  if (!locationId) {
    return { value: 0, label: '' };
  }

  return { value: locationId, label: getLocationTitle(locationId) };
};

export const AddEditUser: React.FC<AddEditUserProps> = ({ editing, userType }) => {
  const params = useParams();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const { addToast } = useToast();
  const { getLocationTitle } = useDataContext();

  const [createUser] = useCreateUserMutation();
  const [updateUser] = useUpdateSFCMutation();
  const [updateParent] = useUpdateParentMutation();
  const {
    data: adminData,
    error: adminError,
    isLoading: adminLoading
  } = useGetAdminQuery(Number(params.id!), {
    skip: !editing || !params.id || userType === 'instructor' || userType === 'customer'
  });
  const {
    data: instructorData,
    error: instructorError,
    isLoading: instructorLoading
  } = useGetInstructorQuery(Number(params.id!), {
    skip: !editing || !params.id || userType !== 'instructor'
  });
  const {
    data: parentData,
    error: parentError,
    isLoading: parentLoading
  } = useGetParentQuery(Number(params.id!), {
    skip: !editing || !params.id || userType !== 'customer'
  });

  const [initialValues, setInitialValues] = useState<FormValues>({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    confirm_password: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    timezone: '',
    location_id: getDefaultLocationId(editing, userType, getLocationTitle),
    user_type: userType.toString(),
    instructor_session_rate: 0,
    instructor_session_reimbursement: 0,
    instructor_online_rate: 0,
    instructor_online_reimbursement: 0,
    trainee_rate: 0,
    observer_rate: 0,
    active: true,
    admin_level: userTypeToAdminLevel(userType),
    test_email_recipient: false
  });

  const validationSchemas = [
    Yup.object({
      first_name: Yup.string().required('First name is required'),
      last_name: Yup.string().required('Last name is required'),
      email: Yup.string().email('Must be a valid email').required('Email is required'),
      ...(!editing && {
        password: Yup.string()
          .min(6, 'Password must be at least 6 characters')
          .required('Password is required')
      }),
      ...(!editing && {
        confirm_password: Yup.string()
          .oneOf([Yup.ref('password')], 'Passwords must match')
          .required('Confirm Password is required')
      }),
      ...(editing &&
        {
          // confirm_password: Yup.string()
          //   .oneOf([Yup.ref('password')], 'Passwords must match')
          //   .when('password', {
          //     is: (password: string) => password && password.trim() !== '',
          //     then: (schema) => schema.required('Confirm Password is required'),
          //     otherwise: (schema) => schema.notRequired()
          //   })
        }),

      phone: Yup.string()
        .matches(phoneRegExp, 'Phone number is not valid')
        .min(10, 'Phone number should have at least 10 digits')
        .required('Phone number is required'),

      address: Yup.string().optional(),
      address2: Yup.string().optional(),
      city: Yup.string().optional(),
      state: Yup.string().optional(),
      zip: Yup.string().optional(),
      timezone: Yup.string()
        .oneOf(usTimezones, 'Please select a valid timezone')
        .required('Time zone is required'),
      // Require location based on the selected form user_type (not the route),
      // so promoting a parent to instructor/admin enforces a location.
      location_id: Yup.mixed().when('user_type', {
        is: (selectedType: string) => selectedType && selectedType !== 'customer',
        then: () =>
          Yup.object({
            value: Yup.number().min(1, 'Location is required').required('Location is required'),
            label: Yup.string().optional()
          }).required('Location is required'),
        otherwise: () =>
          Yup.object({
            value: Yup.number().optional(),
            label: Yup.string().optional()
          })
            .nullable()
            .optional()
      }),
      user_type: Yup.string().required('User type is required'),
      instructor_session_rate: Yup.number().min(0, 'Must be 0 or greater').optional(),
      instructor_session_reimbursement: Yup.number().min(0, 'Must be 0 or greater').optional(),
      instructor_online_rate: Yup.number().min(0, 'Must be 0 or greater').optional(),
      instructor_online_reimbursement: Yup.number().min(0, 'Must be 0 or greater').optional(),
      trainee_rate: Yup.number().min(0, 'Must be 0 or greater').optional(),
      observer_rate: Yup.number().min(0, 'Must be 0 or greater').optional(),
      active: Yup.boolean()
    })
  ];

  useEffect(() => {
    if (editing && adminError && !adminLoading) {
      addToast('Error while loading admin', 'error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing, adminError, adminLoading]);

  useEffect(() => {
    if (editing && instructorError && !instructorLoading) {
      addToast('Error while loading instructor', 'error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing, instructorError, instructorLoading]);

  useEffect(() => {
    if (editing && parentError && !parentLoading) {
      addToast('Error while loading parent', 'error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing, parentError, parentLoading]);

  useEffect(() => {
    let values;
    if (adminData && adminData?.admin) {
      values = adminData.admin;
    } else if (instructorData && instructorData?.instructor) {
      values = instructorData.instructor;
    }
    // TODO: load customer data
    else {
      values = parentData?.parent;
    }

    if (values?.first_name)
      setInitialValues({
        ...values,
        password: '',
        confirm_password: '',
        address: values?.address ?? '',
        address2: values?.address2 && values?.address2.trim() !== '' ? values?.address2 : '',

        location_id: (() => {
          const locationId = values?.location?.id ?? values?.location_id;
          if (!locationId) {
            return null;
          }
          return {
            value: locationId,
            label: values?.location?.title ?? getLocationTitle(locationId)
          };
        })(),
        user_type: getKeyByValue(userTypes, values.user_type_id)!,
        admin_level: userTypeToAdminLevel(
          (getKeyByValue(userTypes, values.user_type_id) as UserType) ?? userType
        )
      });
  }, [adminData, instructorData, parentData]);

  const handleAddUser = async (formValue: FormValues) => {
    try {
      const { confirm_password: _, ...rest } = formValue;
      const selectedUserType = formValue.user_type as UserType;
      const submitData = Object.fromEntries(
        Object.entries({
          ...rest,
          admin_level: userTypeToAdminLevel(selectedUserType)
        }).filter(([, v]) => v !== '')
      ) as typeof rest;

      if (editing) {
        const locationId = Number(formValue.location_id?.value);
        const hasLocation = Number.isFinite(locationId) && locationId > 0;
        // Parent edit route always used update-parent, which never changes user_type_id.
        // When the admin selects a staff role, route through update-sfc instead.
        const isPromotingParent = userType === 'customer' && selectedUserType !== 'customer';

        if (userType === 'customer' && !isPromotingParent) {
          const res = await updateParent({
            id: Number(params.id)!,
            first_name: formValue.first_name,
            last_name: formValue.last_name,
            email: formValue.email,
            ...(formValue.password ? { password: formValue.password } : {}),
            phone: formValue.phone,
            address: formValue.address,
            address2: formValue.address2,
            city: formValue.city,
            state: formValue.state,
            zip: formValue.zip,
            timezone: formValue.timezone,
            location_id: hasLocation ? locationId : null,
            active: Number(formValue.active)
          }).unwrap();
          if (res?.parent) {
            addToast('User updated succesfully', 'success');
            navigate(userLinksMap('customer', params.id!)?.detail ?? '', {
              state: { parent: res.parent }
            });
          }
        } else {
          const isDemotingToCustomer = selectedUserType === 'customer';
          const res = await updateUser({
            ...submitData,
            // Clear location when demoting staff → customer (customers are not location-scoped).
            location_id: isDemotingToCustomer ? null : formValue.location_id,
            admin_level: userTypeToAdminLevel(selectedUserType),
            user_type: selectedUserType,
            id: Number(params.id)!
          }).unwrap();
          if (res && res.user) {
            addToast(`User updated succesfully`, 'success');
            navigate(userLinksMap(selectedUserType, params.id!)?.detail ?? '');
          }
        }
      } else {
        const res = await createUser({
          ...submitData,
          admin_level: userTypeToAdminLevel(selectedUserType)
        }).unwrap();
        if (res && res.user) {
          addToast('User created succesfully', 'success');
          navigate(userLinksMap(selectedUserType, res?.user?.id)?.detail ?? '');
          return;
        } else throw new Error('A problem happened while creating user');
      }
    } catch (e) {
      addToast(getErrorMessage(e), 'error');
    }
  };

  const handleNext = async (
    validateForm: () => Promise<FormikErrors<FormValues>>,
    setTouched: (touched: FormikTouched<FormValues>, shouldValidate?: boolean) => void,
    values: FormValues
  ) => {
    const errors = await validateForm();
    if (Object.keys(errors).length === 0) {
      setCurrentStep(currentStep + 1);
    } else {
      setTouched({
        first_name: true,
        last_name: true,
        email: true,
        password: true,
        confirm_password: true,
        phone: true,
        address: true,
        city: true,
        state: true,
        zip: true,
        timezone: true,
        user_type: true,
        instructor_session_rate: true,
        instructor_session_reimbursement: true,
        instructor_online_rate: true,
        instructor_online_reimbursement: true,
        trainee_rate: true,
        observer_rate: true,
        active: true
      });
    }
  };

  return (
    <FadeIn className="p-lg-4">
      <Stack
        gap={3}
        className="py-lg-0 px-4 py-2"
      >
        <div>
          <BackButton
            text="Back"
            color="#2B4E64"
          />
          <h2
            className="d-none d-lg-block text-info fw-bold mb-0"
            style={{ fontSize: '2.25rem' }}
          >
            {editing ? 'Edit' : 'New'} {userTypeNames[userType]}
          </h2>
          {steps.length > 1 && (
            <Stepper
              steps={steps}
              currentStep={currentStep}
              editing={editing === true}
              onStepClick={(step) => setCurrentStep(step)}
            />
          )}
        </div>

        <div
          className="p-lg-5 p-3"
          style={{
            boxShadow: '0 4px 8px -2px rgba(16, 24, 40, .1),  0 4px 4px 0 rgba(0, 0, 0, .25)',
            border: '1px solid #EBEBEB',
            borderRadius: 10
          }}
        >
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchemas[currentStep]}
            onSubmit={handleAddUser}
            enableReinitialize
          >
            {({
              submitForm,
              isSubmitting,
              validateForm,
              touched,
              errors,
              setTouched,
              values,
              setFieldValue
            }: FormikProps<FormValues>) => (
              <FormikForm className="text-start">
                <div style={{ minHeight: '65vh' }}>
                  {currentStep < steps.length &&
                    React.createElement(steps[currentStep]?.form, {
                      errors,
                      touched,
                      values,
                      setFieldValue,
                      userType,
                      editing
                    })}
                </div>

                <Row className="justify-content-end mt-3">
                  <Col
                    xs={6}
                    lg={3}
                  >
                    {currentStep !== 0 && (
                      <SimpleButton
                        disabled={isSubmitting}
                        type="button"
                        onClick={() => setCurrentStep(currentStep - 1)}
                        className="w-100"
                        variant="outline-primary"
                      >
                        Back
                      </SimpleButton>
                    )}
                  </Col>

                  <Col
                    xs={6}
                    lg={3}
                  >
                    <SimpleButton
                      disabled={isSubmitting}
                      type="button"
                      onClick={
                        currentStep === steps.length - 1
                          ? submitForm
                          : () => handleNext(validateForm, setTouched, values)
                      }
                      className="w-100"
                      variant="primary"
                    >
                      {currentStep === steps.length - 1 ? 'Finish' : 'Continue'}
                    </SimpleButton>
                  </Col>
                </Row>
              </FormikForm>
            )}
          </Formik>
        </div>
      </Stack>
    </FadeIn>
  );
};
